import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AdvanceStatus,
  ApprovalFile,
  PublicAdvance,
  PublicApproval,
  PublicResult,
  approveOrder,
  markAdvancePaid,
  openApprovalFile,
  requestOrderChange,
  viewApproval,
} from "../../../services/orderApprovalApi";
import {
  Lang,
  TextKey,
  day,
  endedMessage,
  fileKind,
  fileSize,
  greetingName,
  money,
  saveLang,
  savedLang,
  serverMessage,
  text,
} from "./approvalText";
import "./orderApproval.scss";

type Mode = "review" | "change";

/** A photo that fails to load shows this plain tile instead of a broken image. */
const PHOTO_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><rect width="4" height="5" fill="#eeeae4"/></svg>');

const showPhotoFallback = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  if (img.src !== PHOTO_FALLBACK) img.src = PHOTO_FALLBACK;
};

/**
 * The customer's approval page (/a/:token). No login, phone first, plain: the order as the studio froze
 * it, then Approve or Request a change. After a decision the page is read-only.
 */
const OrderApprovalPage = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicApproval | null>(null);
  const [failure, setFailure] = useState<{ status: number } | null>(null);
  const [lang, setLang] = useState<Lang>("en");
  const [mode, setMode] = useState<Mode>("review");
  const [name, setName] = useState("");
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  // Optional and unticked. A tick is a yes, "don't send" is a no for both, nothing ticked is no answer
  // (updates continue as before); sent with their answer.
  const [consentWhatsapp, setConsentWhatsapp] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const [noUpdates, setNoUpdates] = useState(false);
  const consentAnswer = () =>
    noUpdates
      ? { consentWhatsapp: false, consentEmail: false }
      : { consentWhatsapp: consentWhatsapp || undefined, consentEmail: consentEmail || undefined };
  // What this visitor just did; a race where someone else decided first shows their decision, not "thanks".
  const [justDecided, setJustDecided] = useState<"APPROVED" | "CHANGES_REQUESTED" | null>(null);

  // Private link: no search listing, no referrer to other sites.
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    const referrer = document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    document.head.appendChild(robots);
    document.head.appendChild(referrer);
    const title = document.title;
    document.title = "Your order details";
    return () => {
      document.head.removeChild(robots);
      document.head.removeChild(referrer);
      document.title = title;
    };
  }, []);

  const accept = useCallback((result: PublicResult, first: boolean) => {
    if (result.ok) {
      setData(result.data);
      setFailure(null);
      if (first) {
        setLang(savedLang(result.data.language === "hi" ? "hi" : "en"));
        setName((result.data.order?.customerName || "").trim());
      }
      return true;
    }
    setFailure({ status: result.status });
    return false;
  }, []);

  const load = useCallback(() => {
    setFailure(null);
    setData(null);
    viewApproval(token || "").then((r) => accept(r, true));
  }, [token, accept]);

  useEffect(load, [load]);

  // The decision card sits at the top; the customer tapped at the bottom. Bring them to it.
  useEffect(() => {
    if (!justDecided) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [justDecided]);

  const switchLang = (next: Lang) => {
    setLang(next);
    saveLang(next);
  };

  const t = (key: Parameters<typeof text>[1], vars?: Record<string, string>) => text(lang, key, vars);

  const failMessage = (status: number, message: string) =>
    status === 429
      ? t("tooMany")
      : status === 403
      ? t("reload")
      : status === 400
      ? serverMessage(lang, message)
      : status >= 500
      ? t("serverError")
      : status === 0
      ? t("offline")
      : t("failed");

  const submitApprove = async () => {
    if (busy || !data?.formToken) return;
    const who = name.trim();
    if (!who) return setFormError(t("nameNeeded"));
    if (who.length < 2) return setFormError(t("nameShort"));
    if (!checked) return setFormError(t("tickNeeded"));
    setBusy(true);
    setFormError("");
    const r = await approveOrder(token || "", {
      name: who,
      checked,
      formToken: data.formToken,
      ...consentAnswer(),
    });
    setBusy(false);
    if (r.ok) {
      setData(r.data);
      // Someone else may have decided first; then the page shows their decision, not "thank you".
      if (r.data.state === "APPROVED" && (r.data.decision?.decidedByName || "") === who) setJustDecided("APPROVED");
    } else {
      setFormError(failMessage(r.status, r.message));
    }
  };

  const submitChange = async () => {
    if (busy || !data?.formToken) return;
    const wanted = note.trim();
    if (!wanted) return setFormError(t("noteNeeded"));
    setBusy(true);
    setFormError("");
    const r = await requestOrderChange(token || "", {
      note: wanted,
      name: name.trim() || undefined,
      formToken: data.formToken,
      ...consentAnswer(),
    });
    setBusy(false);
    if (r.ok) {
      setData(r.data);
      if (r.data.state === "CHANGES_REQUESTED" && (r.data.decision?.note || "") === wanted) setJustDecided("CHANGES_REQUESTED");
    } else {
      setFormError(failMessage(r.status, r.message));
    }
  };

  const header = (
    <header className="oa-head">
      <div className="oa-brand">
        {data?.studio?.logoUrl ? (
          <img className="oa-logo" src={data.studio.logoUrl} alt="" width={44} height={44} decoding="async" />
        ) : null}
        <span className="oa-studio">{data?.studio?.name || ""}</span>
      </div>
      <div className="oa-lang" role="group" aria-label="Language">
        <button type="button" className={lang === "en" ? "is-on" : ""} aria-pressed={lang === "en"} onClick={() => switchLang("en")}>
          English
        </button>
        <button type="button" className={lang === "hi" ? "is-on" : ""} aria-pressed={lang === "hi"} onClick={() => switchLang("hi")} lang="hi">
          हिंदी
        </button>
      </div>
    </header>
  );

  if (failure) {
    const message =
      failure.status === 404
        ? t("notFound")
        : failure.status === 429
        ? t("tooMany")
        : failure.status >= 500
        ? t("serverError")
        : t("offline");
    return (
      <main className="oa-page" lang={lang}>
        {header}
        <section className="oa-card oa-state">
          <p>{message}</p>
          {failure.status !== 404 ? (
            <button type="button" className="oa-btn oa-btn--ghost" onClick={load}>
              {t("retry")}
            </button>
          ) : null}
        </section>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="oa-page" aria-busy="true" lang={lang}>
        {header}
        <p className="oa-muted">{t("loading")}</p>
      </main>
    );
  }

  const ended = endedMessage(data.state, lang);
  if (ended) {
    return (
      <main className="oa-page" lang={lang}>
        {header}
        <section className="oa-card oa-state">
          {data.orderCode ? <p className="oa-code">{t("orderCode")} {data.orderCode}</p> : null}
          <p>{ended}</p>
        </section>
      </main>
    );
  }

  const o = data.order || {};
  const nameError = formError !== "" && (formError === t("nameNeeded") || formError === t("nameShort"));
  const noteError = formError !== "" && (formError === t("noteNeeded") || formError === t("noteTooLong"));
  const currency = o.currency || "INR";
  const decided = data.state === "APPROVED" || data.state === "CHANGES_REQUESTED";
  const lines = (o.lines || []).filter((l) => (l.colour || "").trim() || (l.quantity || "").trim());

  const rows: Array<[string, React.ReactNode]> = [];
  if (o.orderCode || data.orderCode) rows.push([t("orderCode"), o.orderCode || data.orderCode]);
  if (o.productName) rows.push([t("product"), o.productName]);
  if (o.productDesign) rows.push([t("design"), o.productDesign]);
  if (lines.length > 0) {
    rows.push([
      t("colours"),
      <table className="oa-subtable" key="lines">
        <caption className="oa-sr">{t("colourLines")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("colour")}</th>
            <th scope="col">{t("quantity")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={`${i}-${l.colour}`}>
              <td>{l.colour || "—"}</td>
              <td>{l.quantity || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>,
    ]);
  } else if (o.colour) {
    rows.push([t("colour"), o.colour]);
  }
  if (o.quantity) rows.push([t("quantity"), o.quantity]);
  rows.push([t("neededBy"), o.neededBy ? day(o.neededBy, lang) : t("toBeConfirmed")]);
  if (o.city) rows.push([t("deliverTo"), o.city]);
  if (o.specifications) rows.push([t("specs"), <span className="oa-specs">{o.specifications}</span>]);

  const moneyRows: Array<[string, string]> = [];
  const many = !!o.quantity && o.quantity !== "1";
  if (o.unitPrice) moneyRows.push([many ? t("unitPrice") : t("price"), money(o.unitPrice, currency)]);
  if (o.total && many) moneyRows.push([t("total"), money(o.total, currency)]);
  if (o.advance) moneyRows.push([t("advance"), money(o.advance, currency)]);
  if (o.balance) moneyRows.push([t("balance"), money(o.balance, currency)]);

  const consentBoxes = (
    <fieldset className="oa-consent">
      <legend>{t("consentTitle")}</legend>
      <label className="oa-check oa-check--light">
        <input
          type="checkbox"
          checked={consentWhatsapp}
          onChange={(e) => {
            setConsentWhatsapp(e.target.checked);
            if (e.target.checked) setNoUpdates(false);
          }}
        />
        <span>{t("consentWhatsapp")}</span>
      </label>
      <label className="oa-check oa-check--light">
        <input
          type="checkbox"
          checked={consentEmail}
          onChange={(e) => {
            setConsentEmail(e.target.checked);
            if (e.target.checked) setNoUpdates(false);
          }}
        />
        <span>{t("consentEmail")}</span>
      </label>
      <label className="oa-check oa-check--light">
        <input
          type="checkbox"
          checked={noUpdates}
          onChange={(e) => {
            setNoUpdates(e.target.checked);
            if (e.target.checked) {
              setConsentWhatsapp(false);
              setConsentEmail(false);
            }
          }}
        />
        <span>{t("consentNone")}</span>
      </label>
    </fieldset>
  );

  return (
    <main className="oa-page" lang={lang}>
      {header}

      <section className="oa-intro">
        <p className="oa-code">
          {t("orderCode")} {o.orderCode || data.orderCode}
        </p>
        <h1>{greetingName(o) || t("title")}</h1>
        {!decided ? <p className="oa-muted">{t("intro")}</p> : null}
      </section>

      {decided ? (
        <section className={`oa-card oa-decision ${data.state === "APPROVED" ? "is-approved" : "is-change"}`} role="status">
          <p className="oa-decision__lead">
            {data.state === "APPROVED"
              ? justDecided === "APPROVED"
                ? t("thanks")
                : t("approvedOn", { name: data.decision?.decidedByName || "", date: day(data.decision?.decidedAt, lang) })
              : justDecided === "CHANGES_REQUESTED"
              ? t("thanksChange")
              : t("changeOn", { date: day(data.decision?.decidedAt, lang) })}
          </p>
          {data.state === "CHANGES_REQUESTED" && data.decision?.note ? <p className="oa-quote">{data.decision.note}</p> : null}
        </section>
      ) : null}

      <section className="oa-card">
        <table className="oa-table">
          <caption>{t("orderTable")}</caption>
          <thead className="oa-sr">
            <tr>
              <th scope="col">{t("detail")}</th>
              <th scope="col">{t("value")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <th scope="row">{k}</th>
                <td>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {o.preOrderNote ? <p className="oa-note">{o.preOrderNote}</p> : null}
      </section>

      {moneyRows.length > 0 ? (
        <section className="oa-card">
          <table className="oa-table oa-table--money">
            <caption>{t("priceTable")}</caption>
            <thead className="oa-sr">
              <tr>
                <th scope="col">{t("detail")}</th>
                <th scope="col">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {moneyRows.map(([k, v]) => (
                <tr key={k}>
                  <th scope="row">{k}</th>
                  <td>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {o.files && o.files.length > 0 ? <ApprovalFiles token={token || ""} files={o.files} lang={lang} /> : null}

      {o.images && o.images.length > 0 ? (
        <section className="oa-card">
          <h2>{t("photos")}</h2>
          <div className="oa-photos">
            {o.images.map((src, i) => (
              <img
                key={`${i}-${src}`}
                src={src}
                alt={`${t("photos")} ${i + 1}`}
                loading="lazy"
                decoding="async"
                width={400}
                height={500}
                onError={showPhotoFallback}
              />
            ))}
          </div>
        </section>
      ) : null}

      {o.policyLines && o.policyLines.length > 0 ? (
        <section className="oa-card">
          <h2>{t("policies")}</h2>
          <ul className="oa-policies">
            {o.policyLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {!decided && data.state === "PENDING" ? (
        <section className="oa-card oa-act">
          {mode === "review" ? (
            <>
              <label className="oa-check">
                <input
                  type="checkbox"
                  checked={checked}
                  aria-invalid={formError === t("tickNeeded") || undefined}
                  aria-describedby={formError === t("tickNeeded") ? "oa-form-error" : undefined}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span>{t("checked")}</span>
              </label>
              <label className="oa-field">
                <span>{t("yourName")}</span>
                <input
                  value={name}
                  maxLength={120}
                  autoComplete="name"
                  aria-invalid={nameError || undefined}
                  aria-describedby={nameError ? "oa-form-error" : undefined}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              {consentBoxes}
              {formError ? (
                <p className="oa-error" role="alert" id="oa-form-error">
                  {formError}
                </p>
              ) : null}
              <button type="button" className="oa-btn oa-btn--primary" disabled={busy} onClick={submitApprove}>
                {busy ? t("approving") : t("approve")}
              </button>
              <button
                type="button"
                className="oa-btn oa-btn--ghost"
                disabled={busy}
                onClick={() => {
                  setFormError("");
                  setMode("change");
                }}
              >
                {t("change")}
              </button>
            </>
          ) : (
            <>
              <label className="oa-field">
                <span>{t("changeHelp")}</span>
                <textarea
                  value={note}
                  rows={4}
                  maxLength={1000}
                  aria-invalid={noteError || undefined}
                  aria-describedby={noteError ? "oa-form-error" : undefined}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <p className="oa-muted oa-count">{note.length}/1000</p>
              {consentBoxes}
              {formError ? (
                <p className="oa-error" role="alert" id="oa-form-error">
                  {formError}
                </p>
              ) : null}
              <button type="button" className="oa-btn oa-btn--primary" disabled={busy} onClick={submitChange}>
                {busy ? t("sending") : t("sendChange")}
              </button>
              <button
                type="button"
                className="oa-btn oa-btn--ghost"
                disabled={busy}
                onClick={() => {
                  setFormError("");
                  setMode("review");
                }}
              >
                {t("cancel")}
              </button>
            </>
          )}
        </section>
      ) : null}

      {data.advance ? (
        <ApprovalAdvance
          token={token || ""}
          formToken={data.formToken}
          advance={data.advance}
          lang={lang}
          failMessage={failMessage}
          onData={setData}
        />
      ) : null}
    </main>
  );
};

const statusTone: Record<AdvanceStatus, string> = { DUE: "is-orange", CUSTOMER_SAYS_PAID: "is-blue", RECEIVED: "is-green" };
const statusKey: Record<AdvanceStatus, TextKey> = {
  DUE: "advanceDue",
  CUSTOMER_SAYS_PAID: "advanceSaysPaid",
  RECEIVED: "advanceReceived",
};

/** Only a upi:// link may become the pay button; anything else from the server is ignored. */
const safeUpiLink = (link?: string | null): string | null => (link && /^upi:\/\//i.test(link.trim()) ? link.trim() : null);

/** The shared order files. Each Open asks for a short-lived URL at that moment; nothing is fetched ahead. */
const ApprovalFiles = ({ token, files, lang }: { token: string; files: ApprovalFile[]; lang: Lang }) => {
  const [state, setState] = useState<Record<string, "busy" | "error">>({});
  const t = (key: TextKey) => text(lang, key);

  const open = async (file: ApprovalFile) => {
    if (state[file.id] === "busy") return;
    // A tab opened inside the tap is not blocked as a popup; it gets the address once the server answers.
    const win = typeof window.open === "function" ? window.open("", "_blank") : null;
    setState((s) => ({ ...s, [file.id]: "busy" }));
    const r = await openApprovalFile(token, file.id);
    if (r.ok && r.data?.url) {
      setState((s) => {
        const next = { ...s };
        delete next[file.id];
        return next;
      });
      if (win) {
        win.opener = null;
        win.location.href = r.data.url;
      } else {
        window.location.assign(r.data.url);
      }
    } else {
      win?.close();
      setState((s) => ({ ...s, [file.id]: "error" }));
    }
  };

  return (
    <section className="oa-card">
      <h2>{t("files")}</h2>
      <ul className="oa-files">
        {files.map((file) => {
          const kind = fileKind(lang, file.contentType, file.name);
          const size = fileSize(file.size);
          const busy = state[file.id] === "busy";
          return (
            <li key={file.id} className="oa-file">
              <div className="oa-file__text">
                <span className="oa-file__name">{file.name || t("fileOther")}</span>
                <span className="oa-muted oa-file__meta">{[kind, size].filter(Boolean).join(" · ")}</span>
                {state[file.id] === "error" ? (
                  <span className="oa-error" role="alert">
                    {t("fileFailed")}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="oa-btn oa-btn--ghost oa-btn--small"
                disabled={busy}
                aria-label={`${t("open")} ${file.name || t("fileOther")}`}
                onClick={() => open(file)}
              >
                {busy ? t("opening") : t("open")}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const CopyButton = ({ value, lang }: { value: string; lang: Lang }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1600);
    } catch {
      // No clipboard (old browser, insecure page): the value stays selectable on the page.
    }
  };
  return (
    <button type="button" className="oa-copy" onClick={copy} aria-live="polite">
      {done ? text(lang, "copied") : text(lang, "copy")}
    </button>
  );
};

/**
 * The order's advance: amount and status, and while it is due, how to pay and "I've paid". Separate from
 * approving, so it works on an approved page too, and kept quieter than Approve.
 */
const ApprovalAdvance = ({
  token,
  formToken,
  advance,
  lang,
  failMessage,
  onData,
}: {
  token: string;
  formToken?: string;
  advance: PublicAdvance;
  lang: Lang;
  failMessage: (status: number, message: string) => string;
  onData: (data: PublicApproval) => void;
}) => {
  const t = (key: TextKey, vars?: Record<string, string>) => text(lang, key, vars);
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status: AdvanceStatus = advance.status in statusKey ? advance.status : "DUE";
  const due = status === "DUE";
  const pay = due ? advance.payment || null : null;
  const upiLink = safeUpiLink(pay?.upiLink);

  const payRows: Array<[string, string, boolean]> = [];
  if (pay?.upiId) payRows.push([t("upiId"), pay.upiId, true]);
  if (pay?.payeeName) payRows.push([t("payee"), pay.payeeName, false]);
  if (pay?.bankName) payRows.push([t("bank"), pay.bankName, false]);
  if (pay?.accountName) payRows.push([t("accountName"), pay.accountName, false]);
  if (pay?.accountNumber) payRows.push([t("accountNumber"), pay.accountNumber, true]);
  if (pay?.ifsc) payRows.push([t("ifsc"), pay.ifsc, true]);

  const send = async () => {
    if (busy || !formToken) return;
    setBusy(true);
    setError("");
    const r = await markAdvancePaid(token, { formToken, reference: reference.trim() || undefined });
    setBusy(false);
    if (r.ok) {
      onData(r.data);
      setOpen(false);
    } else {
      setError(failMessage(r.status, r.message));
    }
  };

  return (
    <section className="oa-card oa-advance" aria-labelledby="oa-advance-title">
      <h2 id="oa-advance-title">{t("advanceTitle")}</h2>
      <div className="oa-advance__sum">
        <span>
          <span className="oa-muted oa-advance__label">{t("advanceAmount")}</span>
          <strong className="oa-advance__amount">{money(String(advance.amount), advance.currency || "INR")}</strong>
        </span>
        <span className={`oa-chip ${statusTone[status]}`}>{t(statusKey[status])}</span>
      </div>
      {status === "CUSTOMER_SAYS_PAID" && advance.customerReference ? (
        <p className="oa-muted oa-advance__ref">{t("paidRef", { ref: advance.customerReference })}</p>
      ) : null}

      {due ? (
        payRows.length > 0 || pay?.note ? (
          <table className="oa-table oa-table--pay">
            <caption>{t("payDetails")}</caption>
            <thead className="oa-sr">
              <tr>
                <th scope="col">{t("detail")}</th>
                <th scope="col">{t("value")}</th>
              </tr>
            </thead>
            <tbody>
              {payRows.map(([k, v, copyable]) => (
                <tr key={k}>
                  <th scope="row">{k}</th>
                  <td>
                    <span className="oa-pay">
                      <span className="oa-pay__value">{v}</span>
                      {copyable ? <CopyButton value={v} lang={lang} /> : null}
                    </span>
                  </td>
                </tr>
              ))}
              {pay?.note ? (
                <tr>
                  <th scope="row">{t("payNote")}</th>
                  <td className="oa-specs">{pay.note}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : (
          <p className="oa-muted">{t("noPayDetails")}</p>
        )
      ) : null}

      {due ? (
        <div className="oa-advance__actions">
          {upiLink ? (
            <a className="oa-btn oa-btn--ghost oa-btn--link" href={upiLink}>
              {t("payUpi")}
            </a>
          ) : null}
          {formToken && !open ? (
            <button type="button" className="oa-btn oa-btn--ghost" onClick={() => setOpen(true)}>
              {t("paidButton")}
            </button>
          ) : null}
          {formToken && open ? (
            <>
              <label className="oa-field">
                <span>{t("paidRefLabel")}</span>
                <input
                  value={reference}
                  maxLength={60}
                  autoComplete="off"
                  inputMode="text"
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
              {error ? (
                <p className="oa-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button type="button" className="oa-btn oa-btn--ghost" disabled={busy} onClick={send}>
                {busy ? t("paidSending") : t("paidSend")}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default OrderApprovalPage;
